import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "@vds/loaders";
import { Notification } from "@vds/notifications";
import {
  HISTORICALSEARCH_STATICS,
  HISTORICALSEARCH_TEST_IDS,
  FORENSIC_SEARCH_DATEFORMAT_MONTHDATE,
  SEARCH_STATUS_PAYLOAD_DONE,
  SEARCH_STATUS_PAYLOAD_UPLOADING,
  ENTITY_IMAGE
} from "./SearchConstants";
import { ROUTES } from "../../global/constants/routesConstants";
import "./search.css";
import { Tab, Tabs } from "@vds/tabs";
import { Body, Title } from "@vds/typography";
import { UnorderedList, ListItem } from "@vds/lists";
import { convertCamelCaseToText } from "../../global/utility/Utils";
import { TextLinkCaret } from "@vds/buttons";
import moment from "moment";
import { getRecentForensicSearchesRequest,
   deleteForensicSearchResult, getImageUrlFromForensicSearchImageIndex, showNotificationBanner } from "../../store/actions";
import { getStatusOfSearch, hasCountableResults, buildPersonFilterLabel } from "./ForensicSearchUtil";
import LazyImage from "./LazyImage";
import SearchResultLightbox from "./SearchResultLightbox";
import { Icon } from "@vds/icons";
import { Expand } from "../../global/components/floorPlan/styles";
import { Tooltip } from "@mui/material";
import { Modal, ModalBody, ModalFooter } from "@vds/modals";
import { Pagination } from "antd";
import { notificationTypes } from "../../layouts/NotificationConfig";

const SearchResult = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const [apiLoading, setApiLoading] = useState(false);
  const siteReducerSites = useSelector((state) => state.sitesReducer.siteTagList);
  const { forensicSearchResultList, deleteSearchResultResponse, imageIndexFetchResult } = useSelector((state) => state.forensicSearchReducer);
  const [currentForensicSearch, setCurrentForensicSearch] = useState(forensicSearchResultList?.responseData.find((f) => f.id === state?.id));
  const deviceListItem = useSelector((state) => state.deviceReducer);
  const [resultStatus, setResultstatus] = useState(currentForensicSearch ? getStatusOfSearch(currentForensicSearch) : "Fetching...");
  const [searchResults, setSearchResults] = useState(currentForensicSearch?.results || []);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [attemptedFetchOnCurrentPage, setAttemptedFetchOnCurrentPage] = useState(false);
  const ITEMS_PER_PAGE = 10;
  // Backup plan for a missed WS event: REST poll every 10s, capped per page-view so a search
  // that never resolves doesn't poll forever - 6 attempts * 10s = 1 min ceiling.
  const POLL_INTERVAL_MS = 10000;
  const POLL_MAX_ATTEMPTS = 6;
  const pollAttemptsRef = useRef(0);
  // Track poll state transitions to avoid logging "poll stopped" multiple times per cycle
  const prevPollStoppedRef = useRef(false);
  // TS Core silently rejects a duplicate upload request for the same entry within 60s of the
  // original; Provider itself only flips an entry to "failed" ~55s in if it can't reach S3. Any
  // revisit-driven retry (see unblockFailedRetries) fired before this window closes would just
  // get silently dropped by TS Core - wait it out before treating an undefined-status entry as
  // actually stuck vs. still genuinely in-flight.
  const DUPLICATE_REQUEST_GRACE_MS = 60000;
  // Dedup: fovId:imageIndex keys already dispatched, so poll re-triggers don't re-request them.
  const requestedImageKeysRef = useRef(new Set());
  // Dispatch timestamp per key in requestedImageKeysRef, so unblockFailedRetries can tell "just
  // requested, still in-flight" apart from "requested a while ago, plausibly stuck".
  const requestedAtRef = useRef(new Map());
  // Not-yet-resolved keys for the page currently viewed. Cleared on page switch, re-derived
  // from live entry.status each run - avoids a left-behind page's stuck entry keeping the
  // poll loop alive forever for a page nobody's looking at.
  const pendingImageKeysRef = useRef(new Set());
  const pollTimeoutRef = useRef(null);
  // getSearchByID poll failure - stop auto-polling, show one Notification with manual retry.
  const [pollExhausted, setPollExhausted] = useState(false);
  // Snapshot of pendingImageKeysRef at the moment a poll fails - not the same as "currently
  // pending" (which also includes keys just dispatched, no attempt made yet).
  const pollFailurePendingKeysRef = useRef(new Set());
  // Per-fovid fetch failures, so concurrent fovs on a page don't overwrite each other.
  const [failedFovFetches, setFailedFovFetches] = useState({});
  // Display-only suppression for failedFovFetches. Can't clear failedFovFetches per-page since
  // a fovId can span multiple pages - instead, dismiss all of the destination page's fovIds on
  // switch; un-dismissed again once a real response (success or fail) lands.
  const inFlightFovFetchesRef = useRef(new Set());
  // Set on page navigation instead of deciding retries off the stale cached responseResult -
  // backend may have already finished an entry we'd otherwise treat as failed. Consumed once
  // the main effect gets a FRESH response.
  const revisitRefreshPendingRef = useRef(false);

  const schedulePoll = () => {
    if (pollTimeoutRef.current) return;
    if (pollAttemptsRef.current >= POLL_MAX_ATTEMPTS) {
      // Ceiling hit - snapshot which keys are stuck so poll-exhausted banner can show.
      // Don't mark entries as failed (causes inconsistencies) or clear refs - keep them
      // in loading state. Banner will show separately for ceiling hit.
      pollFailurePendingKeysRef.current = new Set(pendingImageKeysRef.current);
      setPollExhausted(true);
      console.log(`[SearchResult] poll ceiling reached for search ${state?.id} - giving up after ${POLL_MAX_ATTEMPTS} attempts. ${pollFailurePendingKeysRef.current.size} entries stuck.`);
      return;
    }
    console.log(`[SearchResult] poll started for search ${state?.id}`);
    pollTimeoutRef.current = setTimeout(() => {
      pollTimeoutRef.current = null;
      pollAttemptsRef.current += 1;
      getSearchStatusAPI();
    }, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    return () => clearTimeout(pollTimeoutRef.current);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  // Reset attempt flag on page change - new page shouldn't show banner until after
  // we've tried to fetch and it actually failed.
  useEffect(() => {
    setAttemptedFetchOnCurrentPage(false);
  }, [currentPage]);

  // Cache may be stale from the list page's own summary-only fetch - same 3-tier precedent as
  // selectPage below (resolved: skip, cached-unresolved: fetch directly, empty: REST).
  useEffect(() => {
    pollAttemptsRef.current = 0;
    if (!pageAlreadyResolved(currentForensicSearch, currentPage - 1)) {
      if (pageHasEntries(currentForensicSearch, currentPage - 1)) {
        unblockFailedRetries(currentForensicSearch, currentPage - 1);
        getImagesFromImageIndex(currentForensicSearch, currentPage - 1);
      } else {
        // Flags this as a revisit so a failed mount-fetch falls back to pageAlreadyResolved.
        revisitRefreshPendingRef.current = true;
        getSearchStatusAPI();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // In-flight: undefined (not started) or uploading. Everything else, incl. "failed", is
  // terminal - but "failed" is excluded from "resolved" so unblockFailedRetries can keep
  // retrying it on every revisit (no grace period, unlike stuck-undefined entries).
  const NON_TERMINAL_ENTRY_STATUSES = [undefined, SEARCH_STATUS_PAYLOAD_UPLOADING];
  const pageAlreadyResolved = (search, pageIndex) => {
    const startIdx = ITEMS_PER_PAGE * pageIndex;
    const entries = search?.results?.slice(startIdx, startIdx + ITEMS_PER_PAGE) || [];
    return (
      entries.length > 0 &&
      entries.every((entry) => !NON_TERMINAL_ENTRY_STATUSES.includes(entry?.status) && entry?.status !== "failed")
    );
  };
  // Cache-hit check for the 3-tier page-select strategy: page has cached entries at all
  // (resolved or not) - distinguishes "cached but unresolved" (skip REST, fetch indexes
  // directly) from "no cache" (needs the REST call to populate it in the first place).
  const pageHasEntries = (search, pageIndex) => {
    const startIdx = ITEMS_PER_PAGE * pageIndex;
    const entries = search?.results?.slice(startIdx, startIdx + ITEMS_PER_PAGE) || [];
    return entries.length > 0;
  };
  const pruneResolvedPendingKeys = (results) => {
    if (!results || pendingImageKeysRef.current.size === 0) return;
    results.forEach((entry) => {
      const key = `${entry.fovId}:${entry.imageIndex}`;
      if (pendingImageKeysRef.current.has(key) && !NON_TERMINAL_ENTRY_STATUSES.includes(entry?.status)) {
        pendingImageKeysRef.current.delete(key);
      }
    });
  };

  const getSiteIdNameFromCCDId = (ccdId) => {
    const device = deviceListItem.companionDeviceList.find((d) => d.id === ccdId);
    if (device) {
      const site = (siteReducerSites?.sites || []).find((s) => s.id === device?.siteids[0]);
      return site ? site.name : ccdId;
    }
    return ccdId;
  };

  useEffect(() => {
    if (forensicSearchResultList?.statusCode === 200) {
      // Ignore the list page's own fetch (no id, summary-only) landing in this shared slice -
      // only re-arm polling if this page still genuinely needs data; already-resolved pages
      // must not get an unnecessary extra getSearchStatusAPI call off unrelated store noise.
      if (!forensicSearchResultList?.isSingleFetch || forensicSearchResultList?.requestedId !== state?.id) {
        if (!pageAlreadyResolved(currentForensicSearch, currentPage - 1) || pendingImageKeysRef.current.size > 0) {
          schedulePoll();
        }
        return;
      }
      setApiLoading(false);
      setPollExhausted(false);
      pollFailurePendingKeysRef.current = new Set();
      const responseResult = forensicSearchResultList?.responseData.find((f) => f.id === state?.id);
      setCurrentForensicSearch(responseResult);
      const statusLower = responseResult?.status.toLocaleLowerCase();
      const isDone = statusLower === SEARCH_STATUS_PAYLOAD_DONE.toLocaleLowerCase();
      const isUploading = statusLower === SEARCH_STATUS_PAYLOAD_UPLOADING.toLocaleLowerCase();
      if (isDone || isUploading) {
        setSearchResults(responseResult?.results || []);
        setResultstatus(getStatusOfSearch(responseResult));
        // Consume the revisit flag against fresh data only, never the stale click-time snapshot.
        if (revisitRefreshPendingRef.current) {
          revisitRefreshPendingRef.current = false;
          unblockFailedRetries(responseResult, currentPage - 1);
        }
        if(responseResult?.totalhits === 0){
          if (isDone) setShowDeleteModal(true);
        }else {
          // Fetch for the page actually being viewed, not always page 1 - else a stuck page-1
          // entry keeps pendingImageKeysRef non-empty forever, polling never stops.
          getImagesFromImageIndex(responseResult, currentPage - 1);
        }
        pruneResolvedPendingKeys(responseResult?.results);
        // Keep polling until done AND this page's dispatched keys are resolved - paging to a
        // new page can trigger a fresh on-demand upload after aggregate status already reads done.
        if (!isDone || pendingImageKeysRef.current.size > 0) {
          prevPollStoppedRef.current = false;
          schedulePoll();
        } else if (!prevPollStoppedRef.current) {
          prevPollStoppedRef.current = true;
          console.log(`[SearchResult] poll stopped for search ${state?.id} - done, all pending resolved`);
        }
      } else {
        let currentSearchDetail = forensicSearchResultList?.responseData.find((f) => f.id === currentForensicSearch?.id);
        setResultstatus(getStatusOfSearch(currentSearchDetail));
        setSearchResults(currentSearchDetail?.results || []);
        schedulePoll();
      }
    } else if (forensicSearchResultList?.statusCode && forensicSearchResultList?.statusCode !== 200) {
      // Keep last known-good searchResults/currentForensicSearch intact - wiping would unmount
      // every LazyImage. Stop polling, show one Notification + manual Retry. Skip entirely if
      // cached data already covers this page (nothing for the user to lose).
      setApiLoading(false);
      setResultstatus(getStatusOfSearch(currentForensicSearch));
      // If we just navigated, pendingImageKeysRef isn't populated for that page yet - fall back
      // to pageAlreadyResolved against last-known-good cache. Flag stays set; Retry consumes it.
      const revisitStillNeedsData = revisitRefreshPendingRef.current && !pageAlreadyResolved(currentForensicSearch, currentPage - 1);
      if (!currentForensicSearch || pendingImageKeysRef.current.size > 0 || revisitStillNeedsData) {
        // Snapshot only currently-pending keys - keys dispatched later shouldn't inherit this failure.
        pollFailurePendingKeysRef.current = new Set(pendingImageKeysRef.current);
        setPollExhausted(true);
        console.log(`[SearchResult] poll stopped for search ${state?.id} - status fetch failed`);
      }
    }
  }, [forensicSearchResultList]);


  // Dispatch one image-fetch action per entry (no fovId grouping).
  const getImagesFromImageIndex = async (responseResult, pageIndex = 0) => {
    if (!responseResult?.results || responseResult.results.length === 0) return;

    // Mark that we've attempted to fetch on this page - banner can now show if all fail.
    if (pageIndex === currentPage - 1) {
      setAttemptedFetchOnCurrentPage(true);
    }

    const startIndex = ITEMS_PER_PAGE * pageIndex;
    const pageEntries = responseResult.results.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    // Re-derive the page-scoped pending set fresh from live status every call, not stale
    // Set membership left over from a prior visit.
    pageEntries.forEach((entry) => {
      if (NON_TERMINAL_ENTRY_STATUSES.includes(entry?.status)) {
        pendingImageKeysRef.current.add(`${entry.fovId}:${entry.imageIndex}`);
      }
    });

    const entriesToFetch = [];
    const loggedKeysThisCall = new Set();
    pageEntries
      .filter((entry) => entry?.status === undefined || entry?.status === "failed")
      .forEach((entry) => {
        const key = `${entry.fovId}:${entry.imageIndex}`;
        if (!entry.fovId || requestedImageKeysRef.current.has(key)) return;
        if (!loggedKeysThisCall.has(key)) {
          console.log(`[SearchResult] index triggered for search ${responseResult.id}, fovid ${entry.fovId}, entry ${entry.imageIndex}`);
          loggedKeysThisCall.add(key);
        }
        requestedImageKeysRef.current.add(key);
        requestedAtRef.current.set(key, Date.now());
        pendingImageKeysRef.current.add(key);
        inFlightFovFetchesRef.current.add(key);
        entriesToFetch.push(entry);
      });

    if (entriesToFetch.length === 0) {
      // Nothing new to dispatch != nothing to do - already-dispatched-but-still-pending keys
      // need repolling too, or dedup keeps them stuck forever without a manual Retry.
      const hasPendingOnThisPage = pageEntries.some((entry) => pendingImageKeysRef.current.has(`${entry.fovId}:${entry.imageIndex}`));
      if (hasPendingOnThisPage) schedulePoll();
      return;
    }

    entriesToFetch.forEach((entry) => {
      dispatch(getImageUrlFromForensicSearchImageIndex({
        id: responseResult.id,
        searchIndex: { fovid: entry.fovId, entryids: [entry.imageIndex] },
      }));
    });
    // Restart poll even if aggregate status already reads done - paging can trigger fresh
    // on-demand uploads.
    schedulePoll();
  };

  // Un-dedupe on explicit page navigation only (Retry semantics), for: failed status,
  // failedFovFetches (client-side failure, backend never got the trigger), or a status still
  // undefined on revisit (backend silently dropped it - can't tell "in progress" apart from
  // "dropped" any other way) ONCE the request's grace period has elapsed - before that, a
  // rapid revisit (e.g. page X -> Y -> X within seconds) would just re-fire a duplicate that
  // TS Core silently rejects anyway, since the original request is still plausibly in-flight.
  const unblockFailedRetries = (responseResult, pageIndex) => {
    const startIndex = ITEMS_PER_PAGE * pageIndex;
    const pageEntries = responseResult?.results?.slice(startIndex, startIndex + ITEMS_PER_PAGE) || [];
    pageEntries.forEach((entry) => {
      const key = `${entry.fovId}:${entry.imageIndex}`;
      const requestedAt = requestedAtRef.current.get(key);
      const gracePeriodElapsed = requestedAt === undefined || Date.now() - requestedAt >= DUPLICATE_REQUEST_GRACE_MS;
      const stuckUnresolved =
        entry?.status === undefined && requestedImageKeysRef.current.has(key) && gracePeriodElapsed;
      if (entry.fovId && (entry?.status === "failed" || failedFovFetches[`${entry.fovId}:${entry.imageIndex}`] || stuckUnresolved)) {
        requestedImageKeysRef.current.delete(key);
        requestedAtRef.current.delete(key);
      }
    });
  };

  const getSearchStatusAPI = () => {
    console.log(`[SearchResult] fetching search status for ${state?.id}`);
    dispatch(getRecentForensicSearchesRequest({ id: state?.id }));
  };

  const handleRetryPoll = () => {
    setPollExhausted(false);
    pollAttemptsRef.current = 0;
    // Without this, the main effect's unblockFailedRetries call (gated on this flag) never runs
    // for the fresh response below - the already-dispatched, still-stuck keys stay deduped in
    // requestedImageKeysRef and getImagesFromImageIndex silently skips them, making Retry a
    // no-op for the very entries it exists to unstick.
    revisitRefreshPendingRef.current = true;
    getSearchStatusAPI();
  };

  // Not persist-blacklisted, so a pre-refresh failure survives in imageIndexFetchResult and
  // would get replayed on mount. Snapshot what's already there per fovid; only react to
  // entries whose reference changes (a new dispatch always produces a new object).
  const seenImageIndexFetchResultRef = useRef(imageIndexFetchResult || {});

  // Failed index-fetch = backend never triggered the upload for that fovid. Keyed by fovid so
  // concurrent fovs don't overwrite each other. Poll pauses while any fovid is failed, resumes
  // once any fovid succeeds.
  useEffect(() => {
    const results = imageIndexFetchResult || {};
    Object.entries(results).forEach(([entryKey, result]) => {
      if (result === seenImageIndexFetchResultRef.current[entryKey]) return;
      if (result?.statusCode == null || result.responseData?.id !== state?.id) return;
      inFlightFovFetchesRef.current.delete(entryKey);
      if (result.statusCode !== 200) {
        setFailedFovFetches((prev) => ({ ...prev, [entryKey]: result.responseData }));
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
      } else {
        setFailedFovFetches((prev) => {
          if (!prev[entryKey]) return prev;
          const next = { ...prev };
          delete next[entryKey];
          return next;
        });
        schedulePoll();
      }
    });
    seenImageIndexFetchResultRef.current = results;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageIndexFetchResult]);

  // Retries every failed fovid at once. Hides notification immediately (doesn't wait for
  // result) - the effect above re-adds any fovid that fails again.
  const handleRetryAllFailedImageFetches = () => {
    setFailedFovFetches({});
    Object.entries(failedFovFetches).forEach(([_key, payload]) => {
      dispatch(getImageUrlFromForensicSearchImageIndex({
        id: payload?.id,
        searchIndex: { fovid: payload?.searchIndex?.fovid, entryids: payload?.searchIndex?.entryids },
      }));
    });
  };

  // Manual counterpart to the auto-retry in pageAlreadyResolved/mount/selectPage.
  const handleRetryFailedEntries = () => {
    unblockFailedRetries(currentForensicSearch, currentPage - 1);
    getImagesFromImageIndex(currentForensicSearch, currentPage - 1);
  };

  const handleDeleteConfirm = () => {
    // Keep the modal open through the delete call - closing now would expose the empty
    // 0-results page + wrong loading copy, and strand the user with no retry on failure.
    const deleteSearchPayload = {
          'id': currentForensicSearch.id,
        }
        setApiLoading(true);
        dispatch(deleteForensicSearchResult(deleteSearchPayload));

  };

  const handleDeleteCancel = () => {
    // 0 results = nothing actionable here either way - navigate to list instead of stranding user.
    setShowDeleteModal(false);
    navigate(ROUTES.SEARCH, { state: { resetToTab: 0 } });
  };

  useEffect(() => {
    if (deleteSearchResultResponse?.statusCode === 200) {
      setApiLoading(false);
      setShowDeleteModal(false);
      dispatch(showNotificationBanner({
        type: notificationTypes[60],
        priority: "success",
        title: "Search Deleted Successfully",
        showNotification: true
      }));
      navigate(ROUTES.SEARCH, { state: { resetToTab: 0 } });
     } else if (deleteSearchResultResponse?.statusCode) {
      // Leave modal open on failure so Yes/No still work, instead of stranding the user.
      setApiLoading(false);
      dispatch(showNotificationBanner({
        type: notificationTypes[61],
        priority: "critical",
        title: "Search Deletion Failed",
        showNotification: true
      }));
     }
  },[deleteSearchResultResponse]);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedEntries = searchResults.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const renderResults = () => {
    const totalPages = Math.ceil(searchResults.length / ITEMS_PER_PAGE);

    return (
      <div className="gridParentContainer">
        {(searchResults.length > 0 || currentForensicSearch?.totalhits > 0) && (
          <div className="result_tablets" data-testid={HISTORICALSEARCH_TEST_IDS.RESULT_TABLETS_TAB_DIV}>
            <Tabs orientation="horizontal" indicatorPosition="bottom" fillContainer={false}>
              {["s"].map(() => (
                <Tab
                  label={getSiteIdNameFromCCDId(currentForensicSearch?.targets?.[0]?.ccdid)}
                  key={`historiclaSearch_${currentForensicSearch?.targets?.[0]?.ccdid}Tab`}
                  data-testid={`historiclaSearch_${currentForensicSearch?.targets?.[0]?.ccdid}Tab`}
                ></Tab>
              ))}
            </Tabs>
          </div>
        )}
        {paginatedEntries.length > 0 && (
          <>
            <div className="hs-results-grid">
              {paginatedEntries.map((entry, index) => {
                const fov = deviceListItem.fovList.find((d) => d.id === entry.fovId);
                // True when a failed entry has been re-dispatched for re-upload and the backend
                // hasn't responded yet. Tells LazyImage to show the loading state instead of
                // keeping the error template visible while the retry is in flight.
                const isBeingRetried = entry?.status === "failed" && pendingImageKeysRef.current.has(`${entry.fovId}:${entry.imageIndex}`);
                const camera = deviceListItem.cameraList.find((c) => c.id === fov?.parentdeviceid);
                const location = camera?.customdata?.location;
                const locationParts = [
                  location?.subarea?.name && location.subarea.name !== 'NA' ? location.subarea.name : null,
                  location?.area?.name || null,
                  location?.floor?.name || null,
                  location?.site?.name || null,
                ].filter(Boolean);
                const locationText = locationParts.join(", ");
                return (
                  <div
                    key={`${entry.imageIndex}-${startIndex + index}`}
                    className="hs-thumbnail-clickable"
                    onClick={() => setLightboxIndex(index)}
                  >
                    <div className="hs-thumb-media">
                      <LazyImage
                        steamId={entry?.imageId}
                        imageFor={ENTITY_IMAGE}
                        entity={entry}
                        isBeingRetried={isBeingRetried}
                        onRefresh={() => {
                          const key = `${entry.fovId}:${entry.imageIndex}`;
                          setSearchResults(prev => prev.map(e =>
                            `${e.fovId}:${e.imageIndex}` === key ? { ...e, status: undefined } : e
                          ));
                          inFlightFovFetchesRef.current.add(key);
                          pendingImageKeysRef.current.add(key);
                          dispatch(getImageUrlFromForensicSearchImageIndex({
                            id: currentForensicSearch.id,
                            searchIndex: { fovid: entry.fovId, entryids: [entry.imageIndex] },
                          }));
                          console.log(`[SearchResult] manual refresh triggered for ${key}`);
                        }}
                      />
                      <Expand className="hs-thumb-expand">
                        <Icon name="fullscreen" surface="light" size="large" />
                      </Expand>
                    </div>
                    <div className="hs-thumb-location">
                      <Body viewport="desktop" size="small">
                        {entry.timestamp ? moment(new Date(entry.timestamp)).format("MMM DD, YYYY h:mm a") : "N/A"}
                      </Body>
                      <div className="hs-thumb-stream-body">
                        <strong className="hs-thumb-stream-label">Stream:</strong>
                        <Tooltip title={fov?.name || "Unknown Stream"} placement="top" arrow disableHoverListener={!fov?.name}>
                          <span className="hs-thumb-stream-name">{fov?.name || "Unknown Stream"}</span>
                        </Tooltip>
                      </div>
                      {locationText && (
                        <Tooltip title={`Location: ${locationText}`} placement="bottom" arrow slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, 2] } }] } }}>
                          <div className="hs-thumb-location-text">
                            <strong>Location:</strong> {locationText}
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {totalPages > 1 && (
              <div className="hs-pagination-bar">
                <Pagination
                  current={currentPage}
                  total={searchResults.length}
                  pageSize={ITEMS_PER_PAGE}
                  showSizeChanger={false}
                  onChange={(newPage) => {
                    // Mark any status="failed" entries on the new page as in-flight to prevent
                    // the retry banner from flashing on page transition. This hides the banner
                    // until getImagesFromImageIndex runs (which will keep them in-flight or clear if already requested).
                    const startIdx = (newPage - 1) * ITEMS_PER_PAGE;
                    const pageEntries = searchResults.slice(startIdx, startIdx + ITEMS_PER_PAGE);
                    pageEntries.forEach(e => {
                      if (e?.status === "failed") {
                        inFlightFovFetchesRef.current.add(`${e.fovId}:${e.imageIndex}`);
                      }
                    });
                    setCurrentPage(newPage);
                    const responseResult = forensicSearchResultList?.responseData.find((f) => f.id === state?.id);
                    // Page-scoped: drop the left page's stuck entries so they don't keep
                    // driving "Fetching..."/poll-keepalive for a page nobody's viewing.
                    pendingImageKeysRef.current.clear();
                    // New page-view - reset the ceiling so a stuck earlier page doesn't count
                    // against this fresh one.
                    pollAttemptsRef.current = 0;
                    // Stop the left page's in-flight poll timer too - it was scheduled against
                    // that page's own need/timing, not this one's. Whichever branch below still
                    // needs data re-arms its own fresh timer (getImagesFromImageIndex/
                    // getSearchStatusAPI both call schedulePoll or trigger it downstream), so
                    // this page's poll cadence starts from when it's actually viewed, not
                    // whatever time happened to be left on the old page's countdown.
                    clearTimeout(pollTimeoutRef.current);
                    pollTimeoutRef.current = null;
                    // Terminal status never reverts - skip the forced fetch if this page's
                    // cache is already fully resolved.
                    if (!pageAlreadyResolved(responseResult, newPage - 1)) {
                      if (pageHasEntries(responseResult, newPage - 1)) {
                        // Cached but unresolved (e.g. status undefined/failed) - the entries
                        // themselves are already here, only their image URLs are missing, so
                        // skip the REST round-trip and go straight to the index fetch. Un-dedupe
                        // first (Retry semantics on explicit navigation, same as unblockFailedRetries'
                        // own contract) - otherwise a page whose event never arrived on a prior visit
                        // stays permanently deduped in requestedImageKeysRef and getImagesFromImageIndex
                        // silently no-ops forever on every later revisit.
                        unblockFailedRetries(responseResult, newPage - 1);
                        getImagesFromImageIndex(responseResult, newPage - 1);
                      } else {
                        // No cache at all for this page yet - need the REST call first to
                        // populate entries; main effect applies unblockFailedRetries +
                        // getImagesFromImageIndex against that fresh response once it lands.
                        revisitRefreshPendingRef.current = true;
                        getSearchStatusAPI();
                      }
                    }
                    // Safe to hide unconditionally - fresh fetch re-sets on failure, or page's
                    // already resolved and has nothing left to fail.
                    setPollExhausted(false);
                    pollFailurePendingKeysRef.current = new Set();
                    // Don't clear failedFovFetches here - a fovId can span multiple pages, so
                    // clearing would wipe another page's still-valid failure signal. Just
                    // dismiss this page's notification display.
                    (responseResult?.results || []).slice((newPage - 1) * ITEMS_PER_PAGE, newPage * ITEMS_PER_PAGE)
                      .forEach((entry) => inFlightFovFetchesRef.current.add(`${entry.fovId}:${entry.imageIndex}`));
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {/* --------------- Breadcrumb Scetion ---------------------- */}
      <div className="breadcrumb-and-heading">
        <TextLinkCaret surface="light" iconPosition="left" onClick={() => navigate(ROUTES.SEARCH, { state: { resetToTab: 0 } })} data-testid={HISTORICALSEARCH_TEST_IDS.BACK_BUTTON}>
          Back
        </TextLinkCaret>
      </div>
      {/* pollExhausted/failedFovFetches stay intact across navigation (not cleared) - revisiting
          a stuck page must still show Retry, since dedup means revisiting alone dispatches
          nothing new and schedulePoll() never re-arms on its own. */}
      {(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const currentPageEntries = searchResults.slice(startIndex, startIndex + ITEMS_PER_PAGE);
        const hasPendingOnCurrentPage = currentPageEntries.some((entry) => pollFailurePendingKeysRef.current.has(`${entry.fovId}:${entry.imageIndex}`));
        // Suppress display while a silently-redispatched retry (see selectPage) is still in
        // flight, to avoid flashing stale failure text before its response comes back.
        const hasFailedFetchOnCurrentPage = currentPageEntries.some((entry) => failedFovFetches[`${entry.fovId}:${entry.imageIndex}`] && !inFlightFovFetchesRef.current.has(`${entry.fovId}:${entry.imageIndex}`));
        // Only show if page isn't already fully resolved from cache - avoids a false
        // "unable to load" with nothing left to load.
        const revisitStillNeedsData = revisitRefreshPendingRef.current && !pageAlreadyResolved(currentForensicSearch, currentPage - 1);
        // Backend-declared entry failure (status:"failed"), distinct from the client-side dispatch
        // failure above. Only banner when the whole page failed - a lone failed thumbnail already
        // shows its own inline icon (LazyImage) and doesn't need a page-level banner too.
        // Don't show banner on initial page load - wait until we've attempted auto-fetch first.
        const hasFailedEntriesOnCurrentPage =
          attemptedFetchOnCurrentPage &&
          currentPageEntries.length > 0 &&
          currentPageEntries.every((entry) => entry?.status === "failed") &&
          !currentPageEntries.some((entry) => inFlightFovFetchesRef.current.has(`${entry.fovId}:${entry.imageIndex}`));
        return (
          <>
            {pollExhausted && (!currentForensicSearch || hasPendingOnCurrentPage || revisitStillNeedsData) && (
              <Notification
                type="error"
                fullBleed={false}
                title="Unable to load search results"
                subtitle="We couldn't process images for this search."
                disableFocus={false}
                hideCloseButton={true}
                buttonData={[{ children: "Retry", onClick: handleRetryPoll }]}
              />
            )}

            {hasFailedFetchOnCurrentPage && (
              <Notification
                type="error"
                fullBleed={false}
                title="Failed to fetch search results"
                subtitle="We couldn't process images for this search."
                disableFocus={false}
                hideCloseButton={true}
                buttonData={[{ children: "Retry", onClick: handleRetryAllFailedImageFetches }]}
              />
            )}

            {!hasFailedFetchOnCurrentPage && hasFailedEntriesOnCurrentPage && (
              <Notification
                type="error"
                fullBleed={false}
                title="Unable to process images"
                subtitle="We couldn't process any images on this page."
                disableFocus={false}
                hideCloseButton={true}
                buttonData={[{ children: "Retry", onClick: handleRetryFailedEntries }]}
              />
            )}
          </>
        );
      })()}

      {(() => {
        const headingText = hasCountableResults(currentForensicSearch)
          ? pendingImageKeysRef.current.size === 0
            ? currentForensicSearch?.totalhits > 300
              ? "Showing most recent 300 of " + currentForensicSearch?.totalhits + " results for:"
              : "Showing all " + currentForensicSearch?.totalhits + " results for:"
            : "Fetching..."
          : convertCamelCaseToText(resultStatus);
        const isHeadingLoading = headingText === "Fetching..." || headingText === "Searching...";
        return (
          <div className={`page-heading-medium${isHeadingLoading ? " hs-loading-text" : ""}`}>
            {headingText}
          </div>
        );
      })()}

      <div className="search-result_tablets-view">
        <div className="column-flex">
          {state?.filters?.type === "vehicle" && (
            <Body size="small" weight="semibold">
              {state?.filters?.vehicle?.length > 0 && (
                <>Show me: {state.filters.vehicle.map(v => convertCamelCaseToText(v)).join(", ")}</>
              )}
              {state?.filters?.colorobject?.length > 0 && (
                <> with {state.filters.colorobject.map(c => convertCamelCaseToText(c)).join(", ")} color(s)</>
              )}
            </Body>
          )}

          {state?.filters?.type === "person" && (
            <Body size="small" weight="semibold">
              {buildPersonFilterLabel(state?.filters)}
            </Body>
          )}

          {state?.filters?.datemax && (
            <Body size="small" weight="semibold">
              on {moment(new Date(state?.filters?.datemin)).format(FORENSIC_SEARCH_DATEFORMAT_MONTHDATE)} to{" "}
              {moment(new Date(state?.filters?.datemax)).format(FORENSIC_SEARCH_DATEFORMAT_MONTHDATE)}
            </Body>
          )}

          {siteReducerSites.sites
            .filter((site) => site && state?.customdata?.selectedSites.includes(site.id))
            .map((s) => {
              return (
                <>
                  <div className="search-result_tablets-view-SiteDiv1">
                    <div className="search-result_tablets-view-SiteDiv2">
                      <Body size="small" weight="semibold">
                        {s?.name}
                      </Body>
                    </div>
                    <UnorderedList>
                      {siteReducerSites.floors
                        .filter((floor) => floor && floor.tagids?.[0] && state?.customdata?.selectedFloors.includes(floor.id) && floor.tagids[0] === s.id)
                        .map((f) => {
                          return (
                            <>
                              <ListItem size="bodySmall">
                                {f?.name}
                                {": " +
                                   siteReducerSites.areas
                                    .filter((area) => area && area.tagids?.[0] && state?.customdata?.selectedAreas?.includes(area.id) && area.tagids[0] === f.id)
                                    .map((a) => a?.name)
                                    .join(", ")}
                              </ListItem>
                            </>
                          );
                        })}
                    </UnorderedList>
                  </div>
                </>
              );
            })}
        </div>
      </div>

      {currentForensicSearch?.status == "fail" && (
        <div className="search_result_Noresult_div" dangerouslySetInnerHTML={{ __html: HISTORICALSEARCH_STATICS.SEARCH_RESULT_WITH_NO_RESPONSE }} />
      )}

      <div
        className={`hs-results-wrapper${lightboxIndex !== null ? " hs-grid-lightbox-open" : ""}`}
        data-testid={HISTORICALSEARCH_TEST_IDS.RESULT_TABLETS_TAB_DIV}
      >
        {apiLoading && !showDeleteModal && <Loader active fullscreen={false} surface="light" />}
        {renderResults()}
      </div>

      <SearchResultLightbox
        opened={lightboxIndex !== null}
        entries={paginatedEntries}
        initialIndex={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
        siteReducerSites={siteReducerSites}
        deviceListItem={deviceListItem}
        pendingImageKeysRef={pendingImageKeysRef}
        onRefresh={() => {
          const entry = paginatedEntries[lightboxIndex ?? 0];
          if (!entry) return;
          const key = `${entry.fovId}:${entry.imageIndex}`;
          setSearchResults(prev => prev.map(e =>
            `${e.fovId}:${e.imageIndex}` === key ? { ...e, status: undefined } : e
          ));
          inFlightFovFetchesRef.current.add(key);
          pendingImageKeysRef.current.add(key);
          dispatch(getImageUrlFromForensicSearchImageIndex({
            id: currentForensicSearch.id,
            searchIndex: { fovid: entry.fovId, entryids: [entry.imageIndex] },
          }));
          console.log(`[SearchResult] manual refresh triggered for ${key}`);
        }}
      />

      <Modal
        opened={showDeleteModal}
        hideCloseButton={true}
        className="modal-confirmation"
        disableOutsideClick={true}
        onClose={handleDeleteCancel}
      >
        <Title size="large" bold primitive="h2" viewport="desktop">
          {HISTORICALSEARCH_STATICS.NO_RESULTS_FOUND_TITLE}
        </Title>
        <ModalBody>
          <Body size="medium" viewport="desktop" primitive="span">
            <p className="modal-confirmation-text">
              Do you want to delete the search and create new Search?
            </p>
            <ModalFooter
              buttonData={{
                primary: {
                  "data-testid": "searchresult_confirmDeleteYesButton",
                  className: "modal-button-width",
                  children: apiLoading ? "Deleting..." : "Yes",
                  disabled: apiLoading,
                  onClick: handleDeleteConfirm,
                },
                close: {
                  disabled: apiLoading,
                  "data-testid": "searchresult_denyDeleteNoButton",
                  children: "No",
                  className: "modal-button-width",
                  onClick: handleDeleteCancel,
                },
              }}
            />
          </Body>
        </ModalBody>
      </Modal>
    </>
  );
};
export default SearchResult;
